import { Vector3 } from 'three';
import type { CombatWorld } from '../combat/CombatWorld';
import type { AbilityWorld } from '../game/AbilityWorld';
import { EYE_HEIGHT } from '../movement/constants';
import type { PlayerController } from '../movement/PlayerController';
import type { MovementTuning } from '../movement/tuning';

/**
 * Hero abilities — movement-only, skill-preserving (GDD balance rules): no
 * checkpoint teleports, no permanent boosts, no hazard immunity. Each hero gets a
 * Q / E / V kit, and each ability is meant to feel *distinct* from the others:
 *   - Vaelith: a dash, a fly-through speed gate, and a flight overdrive.
 *   - Seraphine: Lightstep save, a Bulwark Bell forgiveness ring, and Dawnbreak.
 *   - Brakka: a Seismic Slam, a Battering Charge ram, and an Avalanche overdrive.
 *   - Nyx: a Shadowblink, a Phantom Vault launch, and a Nightfall overdrive.
 * The ultimate (V) is a timed `effect` overdrive + a comet trail + an accent
 * screen-glow. Everything routes through the controller's primitives (blink/surge/
 * boost/dive/hop) and `AbilityWorld`, never bespoke physics. Cooldowns are tracked
 * by `AbilityTracker`, keyed by `AbilityDef.id`.
 */
export interface AbilityContext {
  player: PlayerController;
  /** Current view yaw (radians) — for abilities that key off facing. */
  yaw: number;
  /** Current view pitch (radians) — for abilities that aim where you look. */
  pitch: number;
  /** Spawns/owns transient world objects (gates, ramps, trails). */
  world: AbilityWorld;
  /**
   * Combat sim handle (spec 0025). Present only on combat-enabled maps —
   * practice maps pass none, so damage payloads are provably inert there.
   */
  combat?: CombatWorld;
  /**
   * The caster's combat id (spec 0027). Omitted → 'player'. AI champions pass
   * their own id so the same AbilityDef credits damage (and friendly-fire
   * filters) correctly.
   */
  selfId?: string;
}

/**
 * A timed feel buff. While active it layers absolute `tuning` overrides on top of
 * the hero's resolved tuning, then reverts — a movement-only "ultimate/active"
 * with no checkpoint teleports or permanent gains (GDD balance rules).
 */
export interface AbilityEffect {
  durationSec: number;
  tuning: Partial<MovementTuning>;
  /** Optional screen-edge glow color (hex) shown while active — for ultimates. */
  glow?: number;
}

export interface AbilityDef {
  /** Stable id (also the cooldown key + skill-icon filename stem); unique within a hero. */
  id: string;
  /** HUD slot / keybind. `key` is a KeyboardEvent.code (e.g. 'KeyQ'). */
  slot: 'q' | 'e' | 'r';
  key: string;
  name: string;
  /** Short description for tooltips / future credits. */
  description: string;
  /** Skill-bar icon stem under public/assets/skills/ (e.g. 'phase-lunge'). */
  icon: string;
  /** Cooldown in seconds. */
  cooldown: number;
  /**
   * Instant effect, run once on the rising edge when off cooldown. Optional.
   * Returning `false` means the ability is mid-sequence (e.g. the first click of
   * a two-click cast) and should NOT start its cooldown yet; most abilities
   * return nothing (void).
   */
  // biome-ignore lint/suspicious/noConfusingVoidType: void = normal ability, boolean = two-click skip-cooldown
  activate?(ctx: AbilityContext): void | boolean;
  /** Timed feel buff applied on activation (optional). */
  effect?: AbilityEffect;
}

// ── Shared ability tuning + helpers ────────────────────────────────────────
// Feature-level params (hop lengths / impulse speeds), distinct from the core
// surf-model constants in src/movement/constants.ts.
export const RUNE_BLINK_DISTANCE = 220; // a generic blink distance (also used by blink.test)
export const PHASE_LUNGE_DISTANCE = 110;
export const SHADOW_STEP_DISTANCE = 150;
export const THUNDER_STOMP_SPEED = 900;
export const RADIANT_RECOVER_SPEED = 230;

// Combat payload params (specs 0025/0028) — feature-level, NOT movement constants.
export const SHADOW_DAGGER_DAMAGE = 35;
export const SHADOW_DAGGER_SPEED = 2200;
export const SHADOW_DAGGER_RADIUS = 24;
export const SHADOW_DAGGER_RANGE = 2600;
export const SEISMIC_SLAM_DAMAGE = 50;
export const SEISMIC_SLAM_RADIUS = 280;
export const ARCANE_DART_DAMAGE = 30;
export const ARCANE_DART_SPEED = 2600;
export const ARCANE_DART_RADIUS = 20;
export const ARCANE_DART_RANGE = 3000;
export const RADIANT_NOVA_DAMAGE = 30;
export const RADIANT_NOVA_RADIUS = 240;

/** Hero accent tints (match the roster accents) for spawned VFX / world objects. */
const VAELITH_BLUE = 0x6aa6ff;
const SERAPHINE_GOLD = 0xffd45e;
const BRAKKA_ORANGE = 0xff8a3d;
const NYX_PURPLE = 0xb884ff;

/**
 * Camera-forward unit vector from view yaw + pitch (YXZ), matching what the
 * renderer does with `camera.rotation.set(pitch, yaw, 0, 'YXZ')`. Pitch follows
 * `MouseLook.pitch`: NEGATIVE looks down, so the vertical component is
 * `+sin(pitch)` — see aim.test.ts, which pins this against a real three.js camera.
 */
export function aimForward(yaw: number, pitch: number): Vector3 {
  const cp = Math.cos(pitch);
  return new Vector3(-Math.sin(yaw) * cp, Math.sin(pitch), -Math.cos(yaw) * cp).normalize();
}

/**
 * Direction to place world objects along: the player's actual line of travel
 * (velocity) when moving, else where they aim — so you fly into / drop onto the
 * spawned object instead of past it.
 */
function travelDirection(player: PlayerController, yaw: number, pitch: number): Vector3 {
  const v = player.velocity;
  const speed = Math.hypot(v.x, v.y, v.z);
  return speed > 250
    ? new Vector3(v.x, v.y, v.z).multiplyScalar(1 / speed)
    : aimForward(yaw, pitch);
}

// ═══════════════════ Vaelith — Technical (razor air control) ═══════════════

/**
 * Vaelith Q — Phase Lunge: a dash boost forward (not a teleport). On combat
 * maps the lunge also fires an arcane dart where you aim (spec 0028).
 */
export const PHASE_LUNGE: AbilityDef = {
  id: 'phase-lunge',
  slot: 'q',
  key: 'KeyQ',
  name: 'Phase Lunge',
  description:
    'Dash forward along your heading in a burst of speed, firing an arcane dart where you aim.',
  icon: 'phase-lunge',
  cooldown: 5,
  activate({ player, yaw, pitch, world, combat, selfId }) {
    player.blink(yaw, PHASE_LUNGE_DISTANCE);
    player.boostLateral(1.18);
    player.surgeForward(yaw, 380);
    combat?.spawnProjectile({
      ownerId: selfId ?? 'player',
      origin: new Vector3(player.position.x, player.position.y + EYE_HEIGHT, player.position.z),
      direction: aimForward(yaw, pitch),
      speed: ARCANE_DART_SPEED,
      radius: ARCANE_DART_RADIUS,
      damage: ARCANE_DART_DAMAGE,
      maxDistance: ARCANE_DART_RANGE,
      color: VAELITH_BLUE,
    });
    world.spawnSparkleBurst(player.position, VAELITH_BLUE);
  },
};

/** Vaelith E — Velocity Gate: a speed portal on your path; fly through it for a burst. */
export const VELOCITY_GATE: AbilityDef = {
  id: 'velocity-gate',
  slot: 'e',
  key: 'KeyE',
  name: 'Velocity Gate',
  description: 'Cast an arcane gate on your path ahead — fly through it for a big speed boost.',
  icon: 'velocity-gate',
  cooldown: 10,
  activate({ player, yaw, pitch, world }) {
    world.spawnSpeedPortal('velocity-gate', {
      direction: travelDirection(player, yaw, pitch),
      boost: 1.4,
      durationSec: 8,
      radius: 170,
      distance: 520,
      color: VAELITH_BLUE,
    });
  },
};

const AETHER_APEX_DURATION = 4.5;

/** Vaelith R (ultimate) — Aether Apex: a flight-overdrive state (carve to compound speed). */
export const AETHER_APEX: AbilityDef = {
  id: 'aether-apex',
  slot: 'r',
  key: 'KeyV',
  name: 'Aether Apex',
  description:
    'Flight overdrive (~5s): low gravity, razor air control, a raised speed cap. Carve to compound speed.',
  icon: 'aether-apex',
  cooldown: 32,
  effect: {
    durationSec: AETHER_APEX_DURATION,
    tuning: { gravity: 600, airAccelerate: 205, airCap: 40, friction: 3.0, maxSpeed: 300 },
    glow: VAELITH_BLUE,
  },
  activate({ world }) {
    world.startTrail(AETHER_APEX_DURATION, VAELITH_BLUE);
  },
};

// ═══════════════════ Seraphine — Stabilizer (forgiving) ════════════════════

const DAWNBREAK_DURATION = 6;

/**
 * Seraphine Q — Lightstep: the all-in-one forgiving save. Cancels a bad fall and
 * adds a gentle upward hop plus a small forward nudge to recover a line. (Folds
 * the old Radiant Recover + Featherfall into one clean, reliable button.)
 */
export const LIGHTSTEP: AbilityDef = {
  id: 'radiant-recover',
  slot: 'q',
  key: 'KeyQ',
  name: 'Lightstep',
  description:
    'Cancel a bad fall with a gentle hop and nudge — the radiant burst damages enemies around you.',
  icon: 'radiant-recover',
  cooldown: 5,
  activate({ player, yaw, world, combat, selfId }) {
    if (player.velocity.y < 0) {
      player.velocity.y *= 0.3; // soften the fall the instant you cast
    }
    player.airHop(RADIANT_RECOVER_SPEED);
    player.surgeForward(yaw, 120);
    // Radiant nova (spec 0028): the save also rings damage around the cast point.
    combat?.strikeArea(
      selfId ?? 'player',
      player.position,
      RADIANT_NOVA_RADIUS,
      RADIANT_NOVA_DAMAGE,
    );
    world.spawnSparkleBurst(player.position, SERAPHINE_GOLD);
  },
};

/**
 * Seraphine E — Bulwark Bell: rings a steadying bell. Opens a ~2.5s forgiveness
 * window where even sloppy strafes keep gaining speed (high air accel + cap) —
 * the stabilizer's "breathe and reset" button.
 */
export const BULWARK_BELL: AbilityDef = {
  id: 'bulwark-bell',
  slot: 'e',
  key: 'KeyE',
  name: 'Bulwark Bell',
  description: 'Open a forgiveness window where even sloppy strafes keep gaining speed.',
  icon: 'bulwark-bell',
  cooldown: 11,
  effect: { durationSec: 2.5, tuning: { airAccelerate: 185, airCap: 38 } },
  activate({ player, world }) {
    world.spawnSparkleBurst(player.position, SERAPHINE_GOLD, { count: 60, life: 0.6, radius: 80 });
  },
};

/**
 * Seraphine R (ultimate) — Dawnbreak: a long, forgiving overdrive — soft gravity,
 * steady air and speed retention to settle your line. Evolves Path of Light.
 */
export const DAWNBREAK: AbilityDef = {
  id: 'path-of-light',
  slot: 'r',
  key: 'KeyV',
  name: 'Dawnbreak',
  description: 'A long, forgiving overdrive: soft gravity, steady air and speed retention.',
  icon: 'path-of-light',
  cooldown: 30,
  effect: {
    durationSec: DAWNBREAK_DURATION,
    tuning: { gravity: 560, airAccelerate: 180, airCap: 38, friction: 2.2, maxSpeed: 272 },
    glow: SERAPHINE_GOLD,
  },
  activate({ world }) {
    world.startTrail(DAWNBREAK_DURATION, SERAPHINE_GOLD);
  },
};

// ═══════════════════ Brakka — Momentum (heavy, unstoppable) ════════════════

const BATTERING_CHARGE_DURATION = 1.2;
const AVALANCHE_DURATION = 5;

/**
 * Brakka Q — Seismic Slam: slam down to reconnect with a ramp and reset a botched
 * jump, with a heavy ground-shock burst of VFX on impact. Never slows your descent.
 * On combat maps the shock also strikes everything around the cast point
 * (spec 0025; on-landing timing is an M2 question).
 */
export const SEISMIC_SLAM: AbilityDef = {
  id: 'thunder-stomp',
  slot: 'q',
  key: 'KeyQ',
  name: 'Seismic Slam',
  description:
    'Slam downward to force a landing and reconnect with a ramp — the shock damages everything around you. Never slows your descent.',
  icon: 'thunder-stomp',
  cooldown: 5,
  activate({ player, world, combat, selfId }) {
    player.diveDown(THUNDER_STOMP_SPEED);
    combat?.strikeArea(
      selfId ?? 'player',
      player.position,
      SEISMIC_SLAM_RADIUS,
      SEISMIC_SLAM_DAMAGE,
    );
    world.spawnSparkleBurst(player.position, BRAKKA_ORANGE, {
      count: 120,
      life: 0.5,
      size: 20,
      radius: 90,
    });
  },
};

/**
 * Brakka E — Battering Charge: a committed forward ram. A heavy burst of speed
 * along your heading, but for a moment you can barely steer — pick your line,
 * then commit.
 */
export const BATTERING_CHARGE: AbilityDef = {
  id: 'iron-charge',
  slot: 'e',
  key: 'KeyE',
  name: 'Battering Charge',
  description: 'Ram forward in a heavy burst of speed — but you can barely steer for a moment.',
  icon: 'iron-charge',
  cooldown: 8,
  effect: { durationSec: BATTERING_CHARGE_DURATION, tuning: { airAccelerate: 60 } },
  activate({ player, yaw, world }) {
    player.boostLateral(1.3);
    player.surgeForward(yaw, 280);
    world.spawnSparkleBurst(player.position, BRAKKA_ORANGE, {
      count: 90,
      life: 0.5,
      size: 18,
      radius: 80,
    });
  },
};

/**
 * Brakka R (ultimate) — Avalanche: near-unstoppable momentum — near-zero friction,
 * a higher speed ceiling and a forward kick.
 */
export const AVALANCHE: AbilityDef = {
  id: 'avalanche-run',
  slot: 'r',
  key: 'KeyV',
  name: 'Avalanche',
  description:
    'Become an avalanche: near-zero friction, a higher speed ceiling and a forward kick.',
  icon: 'avalanche-run',
  cooldown: 30,
  effect: {
    durationSec: AVALANCHE_DURATION,
    tuning: { friction: 0.7, stopSpeed: 40, maxSpeed: 312, airAccelerate: 130 },
    glow: BRAKKA_ORANGE,
  },
  activate({ player, yaw, world }) {
    player.surgeForward(yaw, 220);
    world.startTrail(AVALANCHE_DURATION, BRAKKA_ORANGE);
  },
};

// ═══════════════════ Nyx — Shortcut (nimble, springy) ══════════════════════

const NIGHTFALL_DURATION = 4;

/**
 * Nyx Q — Shadowblink: a snappy short blink along the heading, preserving velocity
 * — tighter than a dash, for edge-hugging shortcut lines. On combat maps the
 * blink also hurls a shadow dagger where you aim (spec 0025).
 */
export const SHADOWBLINK: AbilityDef = {
  id: 'shadow-step',
  slot: 'q',
  key: 'KeyQ',
  name: 'Shadowblink',
  description:
    'A short, snappy blink along your heading that hurls a shadow dagger where you aim — tighter than a dash.',
  icon: 'shadow-step',
  cooldown: 3,
  activate({ player, yaw, pitch, world, combat, selfId }) {
    player.blink(yaw, SHADOW_STEP_DISTANCE);
    player.boostLateral(1.1);
    combat?.spawnProjectile({
      ownerId: selfId ?? 'player',
      origin: new Vector3(player.position.x, player.position.y + EYE_HEIGHT, player.position.z),
      direction: aimForward(yaw, pitch),
      speed: SHADOW_DAGGER_SPEED,
      radius: SHADOW_DAGGER_RADIUS,
      damage: SHADOW_DAGGER_DAMAGE,
      maxDistance: SHADOW_DAGGER_RANGE,
      color: NYX_PURPLE,
    });
    world.spawnSparkleBurst(player.position, NYX_PURPLE, {
      count: 80,
      life: 0.4,
      size: 13,
      radius: 45,
    });
  },
};

/**
 * Nyx E — Phantom Vault: a big upward-and-forward launch off nothing. Pop over a
 * wall or across a gap to steal a greedy vertical shortcut.
 */
export const PHANTOM_VAULT: AbilityDef = {
  id: 'spring-vault',
  slot: 'e',
  key: 'KeyE',
  name: 'Phantom Vault',
  description:
    'Launch up and forward off nothing — pop over a wall or across a gap for a shortcut.',
  icon: 'spring-vault',
  cooldown: 7,
  activate({ player, yaw, world }) {
    player.airHop(340); // big vertical pop
    player.surgeForward(yaw, 200); // carry the launch forward
    world.spawnSparkleBurst(player.position, NYX_PURPLE, {
      count: 70,
      life: 0.45,
      size: 14,
      radius: 55,
    });
  },
};

/**
 * Nyx R (ultimate) — Nightfall: a nimble agility overdrive (razor air, springy
 * jumps) and a launch kick, to commit to a risky shortcut line.
 */
export const NIGHTFALL: AbilityDef = {
  id: 'nightfall',
  slot: 'r',
  key: 'KeyV',
  name: 'Nightfall',
  description: 'Kick into a springy, razor-agile state to commit to a risky shortcut line.',
  icon: 'nightfall',
  cooldown: 26,
  effect: {
    durationSec: NIGHTFALL_DURATION,
    tuning: { airAccelerate: 215, airCap: 42, jumpVelocity: 300, friction: 2.2 },
    glow: NYX_PURPLE,
  },
  activate({ player, yaw, world }) {
    player.airHop(200);
    player.surgeForward(yaw, 150);
    world.startTrail(NIGHTFALL_DURATION, NYX_PURPLE);
  },
};
